import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { UserRole, Permission } from '../types/permissions';
import { TokenService } from '../lib/tokenService';
import { useUserData } from './AuthContext';
import { sendToTerminal } from '../lib/terminalLogger';
import { mapPermissionIdToRole, isValidPermissionId, PERMISSION_ID_TO_ROLE, roleToPermissionId, getMappedRoleByPermissionId } from '../lib/permissionMapper';
import { api } from '../lib/api';
import { validateWebViewMessage } from '../lib/webview/messageValidator';
import { STORAGE_KEYS, migrateStorageKey } from '../lib/storage/constants';
import { resolveInitialActivePermission } from '../lib/permissions/activePermissionResolver';

// 🛡️ SEGURANÇA: Validação de origem para mensagens WebView
// Validação de origem - Importada do módulo centralizado de segurança
import { isAllowedOrigin } from '../lib/security/validators/url.validator';
import { loadFeaturesManifest } from '../lib/permissions/featuresManifest';
import { socketService } from '../lib/socketService';
import { validateMeResponse } from '../lib/auth/validators';
import { useWebSocketConnection } from './WebSocketContext';

// Interface para a configuração de permissões recebida da API (Feature Flag Registry)
// Novo formato normalizado: features é um Record<feature_key, boolean>
// Exemplos: 'tool.oneregua', 'general.can_save', 'calculator.can_export'
interface PermissionConfig {
  perm_id: number;
  role_name?: string | null;
  features: Record<string, boolean>;
}

interface UserInfo {
  id: number;
  nome: string;
  login?: string;  // ✅ NOVO: username único para auditoria
  tipo?: string;
  isAdmin?: boolean;
  permissions?: Array<{
    perm_id: number;
    perm_descricao: string;
    perm_path: string;
  }>;
  permissionsConfig?: PermissionConfig[];
}

function decodeJwtPermId(jwtToken?: string): number | null {
  if (!jwtToken) return null;

  try {
    const parts = String(jwtToken).split('.');
    if (parts.length < 2) return null;

    const payloadJson = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
    const payload = JSON.parse(payloadJson || '{}');

    return typeof payload?.perm_id === 'number' ? payload.perm_id : null;
  } catch {
    return null;
  }
}

interface PermissionContextType {
  // Papel efetivo em uso na UI (considera impersonação se ativa)
  // null quando ainda não foi resolvido (carregando ou sem permissão válida)
  role: UserRole | null;
  // Papel base vindo da API/login (sem impersonação)
  // null quando ainda não foi resolvido
  baseRole: UserRole | null;
  // Papel de impersonação (somente admins), null quando desativado
  impersonatedRole: UserRole | null;
  setImpersonatedRole: (role: UserRole | null) => void;
  // null enquanto permissionsLoading === true ou quando não há permissão válida
  permissions: Permission | null;
  permissionConfig: PermissionConfig | null;
  userInfo: UserInfo | null;
  setRole: (role: UserRole) => void;
  // Informações de simulação
  isSimulating: boolean;
  simulationLoading: boolean;
  // ✅ NOVO: Suporte a múltiplas permissões
  availableRoles: UserRole[]; // Todas as roles disponíveis para o usuário
  availableConfigs: PermissionConfig[]; // Todos os configs disponíveis
  canSwitchRole: boolean; // Se o usuário pode trocar entre roles
  setActiveRole: (role: UserRole) => void; // Função para trocar role ativo
  permissionsLoading: boolean; // true enquanto permissionsConfig não foi carregado do backend
}

const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

// DEPRECATED: Use mapPermissionIdToRole from '../lib/permissionMapper' instead
// Mantido para compatibilidade durante migração
/** @deprecated Use mapPermissionIdToRole from '../lib/permissionMapper' instead */
export const permissionIdToRole: Record<number, UserRole> = PERMISSION_ID_TO_ROLE;

/**
 * Lê o estado inicial de autenticação do TokenService de forma SÍNCRONA.
 *
 * Chamada via lazy initializer do useState, garante que o primeiro render já
 * contenha role e permissions corretos — eliminando o flash de permissões erradas.
 *
 * Retorna null quando o usuário não está logado (sem token / token inválido).
 */
function readInitialAuthFromStorage() {
  try {
    const parsedData = TokenService.getUserData();
    if (!parsedData?.data?.usuario?.id || !parsedData?.data?.usuario?.nome) return null;

    const permissionsConfig: PermissionConfig[] = parsedData.data.permissionsConfig || [];
    const userPermissions: any[] = parsedData.data.permissions || [];

    // [DEBUG-EMBEDDED] Log do que o PermissionContext lê do storage na inicialização
    const firstCfg = permissionsConfig[0];
    const featCount = firstCfg ? Object.keys(firstCfg.features || {}).length : 0;
    sendToTerminal('🔎 [PERMISSION_INIT] readInitialAuthFromStorage', {
      userId: parsedData.data.usuario.id,
      userName: parsedData.data.usuario.nome,
      isAdmin: parsedData.data.isAdmin,
      permissionsConfigCount: permissionsConfig.length,
      firstPermId: firstCfg?.perm_id,
      firstRoleName: firstCfg?.role_name,
      featuresCount: featCount,
      featuresEmpty: featCount === 0,
      hasClientSection: firstCfg?.features?.['general.can_access_client_section'],
      hasAccessTools: firstCfg?.features?.['general.can_access_tools'],
      userPermissionsCount: userPermissions.length,
    });

    if (permissionsConfig.length === 0) return null;

    const availableConfigs = permissionsConfig.filter((c: PermissionConfig) => isValidPermissionId(c.perm_id));
    const savedPermIdRaw = localStorage.getItem(STORAGE_KEYS.ACTIVE_PERM_ID);
    const savedPermId = savedPermIdRaw ? Number(savedPermIdRaw) : null;
    const jwtPermId = decodeJwtPermId(TokenService.getAccessToken() || undefined);
    const initialPermId = resolveInitialActivePermission({
      permissionsConfig,
      userPermissions,
      jwtPermId,
      savedPermId,
    });
    if (initialPermId === null) return null;

    const config = permissionsConfig.find(c => c.perm_id === initialPermId);
    if (!config) return null;

    const mappedRole = mapPermissionIdToRole(initialPermId);

    return {
      baseRole: mappedRole,
      role: mappedRole,
      permissionConfig: config,
      availableConfigs,
      activePermId: initialPermId,
      permissions: {
        role: mappedRole,
        features: {
          canValidate: config.features?.['general.can_validate'] ?? false,
          canSave: config.features?.['general.can_save'] ?? false,
          canExport: config.features?.['general.can_export'] ?? false,
          canExportLayoutPng: config.features?.['general.can_export_layout_png'] ?? false,
          canViewReports: config.features?.['general.can_view_reports'] ?? false,
          canAccessTools: config.features?.['general.can_access_tools'] ?? false,
          canAccessCalendar: config.features?.['general.can_access_calendar'] ?? false,
          showSimplifiedInterface: config.features?.['general.show_simplified_interface'] ?? true,
        },
      } as Permission,
      userInfo: {
        id: parsedData.data.usuario.id,
        nome: parsedData.data.usuario.nome,
        login: parsedData.data.usuario.login,
        isAdmin: parsedData.data.isAdmin,
        permissions: userPermissions,
        permissionsConfig,
      } as UserInfo,
    };
  } catch {
    return null;
  }
}

/**
 * Provider de permissões e papel do usuário.
 * Re-hidrata dados do TokenService e suporta impersonação para admins.
 */
export function PermissionProvider({ children }: { children: React.ReactNode }) {
  const { isEnabled: isWebSocketEnabled } = useWebSocketConnection();
  const initializedRef = useRef(false);
  const noConfigLoggedRef = useRef(false); // Evita log duplicado "API não retornou config"

  // Fonte única de verdade para userData: AuthContext.
  // Quando muda (login/refresh/logout/updateUserData), o useEffect abaixo
  // re-deriva todo o estado de permissões — sem precisar escutar eventos globais.
  const authUserData = useUserData();

  // Leitura síncrona no primeiro render — previne flash de permissões erradas.
  // O lazy initializer do useState roda UMA vez antes do primeiro paint.
  const [initState] = useState(() => readInitialAuthFromStorage());

  // Estado inicializado a partir da leitura síncrona (se disponível).
  // Quando não há initState (usuário ainda não autenticado / dados não carregados),
  // o estado fica null e permissionsLoading === true — sem fallback para 'cliente'.
  const [baseRole, setBaseRole] = useState<UserRole | null>(initState?.baseRole ?? null);
  const [role, setRole] = useState<UserRole | null>(initState?.role ?? null);
  const [permissions, setPermissions] = useState<Permission | null>(
    initState?.permissions ?? null
  );
  const [permissionConfig, setPermissionConfig] = useState<PermissionConfig | null>(
    initState?.permissionConfig ?? null
  );
  const [userInfo, setUserInfo] = useState<UserInfo | null>(initState?.userInfo ?? null);
  const [impersonatedRole, setImpersonatedRoleState] = useState<UserRole | null>(null);

  // Estados para múltiplas permissões
  const [availableConfigs, setAvailableConfigs] = useState<PermissionConfig[]>(
    initState?.availableConfigs ?? []
  );
  const [activePermId, setActivePermId] = useState<number | null>(initState?.activePermId ?? null);

  // Loading state: true até permissionsConfig ter features reais do backend
  const [permissionsLoading, setPermissionsLoading] = useState(() => {
    if (!initState?.permissionConfig) return true;
    const featCount = Object.keys(initState.permissionConfig.features || {}).length;
    return featCount === 0;
  });

  // Estados de simulação
  const [simulationLoading, setSimulationLoading] = useState(false);
  const [simulatedConfig, setSimulatedConfig] = useState<PermissionConfig | null>(null);
  const simulationCacheRef = useRef<Map<UserRole, { config: PermissionConfig; timestamp: number }>>(new Map());
  const SIMULATION_CACHE_TTL = 30000; // 30 segundos

  // Carregar impersonação do storage (se for admin)
  useEffect(() => {
    try {
      // ✅ MIGRAÇÃO AUTOMÁTICA: impersonateRole → cdr:auth:impersonateRole
      migrateStorageKey(STORAGE_KEYS.LEGACY.IMPERSONATE_ROLE, STORAGE_KEYS.IMPERSONATE_ROLE);

      const saved = localStorage.getItem(STORAGE_KEYS.IMPERSONATE_ROLE);

      if (saved) {
        const loadedRole = saved as UserRole;

        // silencioso

        // 🚨 VALIDAÇÃO: Verificar se a impersonação é válida
        setTimeout(() => {
          const currentUserData = TokenService.getUserData();
          const userIsAdmin = currentUserData?.data?.isAdmin;
          const userPermId = currentUserData?.data?.permissions?.[0]?.perm_id;

          // silencioso

          // 🚨 CORREÇÃO AUTOMÁTICA: Se impersonação é inválida, remover
          if (!userIsAdmin || (userPermId === 2 && loadedRole !== 'admin')) {
            localStorage.removeItem(STORAGE_KEYS.IMPERSONATE_ROLE);
            setImpersonatedRoleState(null);

          } else {
            setImpersonatedRoleState(loadedRole);
          }
        }, 100); // Delay para permitir que userInfo seja carregado
      } else {
        // silencioso
      }
    } catch (error) {
      // silencioso
    }
  }, []);

  // Função para buscar permissões da API para simulação
  const fetchSimulatedPermissions = useCallback(async (roleToSimulate: UserRole) => {
    const permId = roleToPermissionId(roleToSimulate);
    if (!permId) {
      sendToTerminal('⚠️ [SIMULATION] Role sem perm_id mapeado', { role: roleToSimulate });
      return null;
    }

    // Verificar cache
    const cached = simulationCacheRef.current.get(roleToSimulate);
    if (cached && Date.now() - cached.timestamp < SIMULATION_CACHE_TTL) {
      sendToTerminal('✅ [SIMULATION] Usando cache', { role: roleToSimulate, cacheAge: Date.now() - cached.timestamp });
      return cached.config;
    }

    try {
      setSimulationLoading(true);
      sendToTerminal('🔄 [SIMULATION] Buscando permissões da API', { role: roleToSimulate, permId });

      const response = await api(`/permissions/config/${permId}`, { method: 'GET' });

      if (response?.success && response?.data) {
        const config = response.data as PermissionConfig;

        // Salvar no cache
        simulationCacheRef.current.set(roleToSimulate, { config, timestamp: Date.now() });

        sendToTerminal('✅ [SIMULATION] Permissões carregadas da API', {
          role: roleToSimulate,
          permId,
          canSave: config.features?.['general.can_save'],
          canExport: config.features?.['general.can_export']
        });

        return config;
      }

      sendToTerminal('⚠️ [SIMULATION] API retornou resposta inválida', { response });
      return null;
    } catch (error) {
      sendToTerminal('❌ [SIMULATION] Erro ao buscar permissões', {
        role: roleToSimulate,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      return null;
    } finally {
      setSimulationLoading(false);
    }
  }, []);

  const setImpersonatedRole = useCallback(async (newRole: UserRole | null) => {
    // Limpar config simulada anterior
    if (!newRole) {
      setSimulatedConfig(null);
    }

    setImpersonatedRoleState(newRole);

    try {
      if (newRole) {
        localStorage.setItem(STORAGE_KEYS.IMPERSONATE_ROLE, newRole);

        // Buscar permissões reais da API para o papel simulado
        const config = await fetchSimulatedPermissions(newRole);
        if (config) {
          setSimulatedConfig(config);
        }
      } else {
        localStorage.removeItem(STORAGE_KEYS.IMPERSONATE_ROLE);
        setSimulatedConfig(null);
      }
    } catch (error) {
      // silencioso
    }
  }, [fetchSimulatedPermissions]);

  // ✅ NOVO: Função para trocar role ativo (múltiplas permissões)
  const setActiveRole = useCallback((newRole: UserRole) => {
    try {
      // Validar se o role está disponível
      const permId = roleToPermissionId(newRole);
      if (!permId) {
        sendToTerminal('❌ [PERMISSION] Role sem perm_id mapeado', { role: newRole });
        return;
      }

      // Verificar se o config existe nos disponíveis
      const config = availableConfigs.find(c => c.perm_id === permId);
      if (!config) {
        sendToTerminal('❌ [PERMISSION] Config não encontrado para role', {
          role: newRole,
          permId,
          availablePermIds: availableConfigs.map(c => c.perm_id)
        });
        return;
      }

      // Atualizar role e config ativo
      setBaseRole(newRole);
      setRole(newRole);
      setPermissionConfig(config);
      setActivePermId(permId);

      // Salvar preferência
      localStorage.setItem(STORAGE_KEYS.ACTIVE_PERM_ID, permId.toString());

      // Atualizar permissões
      setPermissions({
        role: newRole,
        features: {
          canValidate: config.features?.['general.can_validate'] ?? false,
          canSave: config.features?.['general.can_save'] ?? false,
          canExport: config.features?.['general.can_export'] ?? false,
          canExportLayoutPng: config.features?.['general.can_export_layout_png'] ?? false,
          canViewReports: config.features?.['general.can_view_reports'] ?? false,
          canAccessTools: config.features?.['general.can_access_tools'] ?? false,
          canAccessCalendar: config.features?.['general.can_access_calendar'] ?? false,
          showSimplifiedInterface: config.features?.['general.show_simplified_interface'] ?? true,
        }
      });

      // Disparar evento para componentes ouvirem
      window.dispatchEvent(new CustomEvent('role:changed', { detail: newRole }));

      // Log simplificado - apenas informações essenciais
      sendToTerminal(`✅ [PERMISSION] Role alterado: ${config.role_name || newRole}`, {
        newRole,
        permId
      });
    } catch (error) {
      sendToTerminal('❌ [PERMISSION] Erro ao trocar role', {
        error: error instanceof Error ? error.message : 'Erro desconhecido',
        role: newRole
      });
    }
  }, [availableConfigs]);

  // ✅ NOVO: Calcular roles disponíveis e se pode trocar
  const availableRoles = useMemo(() => {
    return availableConfigs
      .map(config => getMappedRoleByPermissionId(config.perm_id))
      .filter((role): role is UserRole => role !== null);
  }, [availableConfigs]);

  const canSwitchRole = useMemo(() => {
    return availableConfigs.length > 1 && !impersonatedRole; // Não permite trocar durante impersonação
  }, [availableConfigs.length, impersonatedRole]);

  // ✅ NOVO: Função auxiliar para determinar perm_id inicial
  const determineInitialPermId = useCallback((
    userPermissions: any[],
    permissionsConfig: PermissionConfig[],
    jwtToken?: string
  ): number | null => {
    let savedPermId: number | null = null;
    try {
      const savedPermIdRaw = localStorage.getItem(STORAGE_KEYS.ACTIVE_PERM_ID);
      if (savedPermIdRaw) savedPermId = Number(savedPermIdRaw);
    } catch { }

    return resolveInitialActivePermission({
      permissionsConfig,
      userPermissions,
      jwtPermId: decodeJwtPermId(jwtToken),
      savedPermId,
      onLog: sendToTerminal,
    });
  }, []);

  // Função pública para re-hidratar do storage sempre que auth mudar
  const applyAuthFromStorage = useCallback(() => {
    // Garante que o manifest seja carregado após login (caso tenha falhado no boot por falta de token)
    loadFeaturesManifest();
    try {
      const parsedData = TokenService.getUserData();
      if (!parsedData?.data?.usuario) return;
      const userPermissions = parsedData.data.permissions;
      const permissionsConfig = parsedData.data.permissionsConfig || [];

      // [DEBUG-EMBEDDED] Log quando auth:updated re-hidrata as permissões
      const cfgFirst = permissionsConfig[0];
      const fCount = cfgFirst ? Object.keys(cfgFirst.features || {}).length : 0;
      sendToTerminal('🔎 [PERMISSION_REFRESH] applyAuthFromStorage (auth:updated)', {
        userId: parsedData.data.usuario.id,
        userName: parsedData.data.usuario.nome,
        permissionsConfigCount: permissionsConfig.length,
        firstPermId: cfgFirst?.perm_id,
        featuresCount: fCount,
        featuresEmpty: fCount === 0,
        hasClientSection: cfgFirst?.features?.['general.can_access_client_section'],
        hasAccessTools: cfgFirst?.features?.['general.can_access_tools'],
      });

      // Armazenar apenas configs de perm_ids conhecidos pelo frontend
      const knownConfigs = permissionsConfig.filter((c: PermissionConfig) => isValidPermissionId(c.perm_id));
      setAvailableConfigs(knownConfigs.length > 0 ? knownConfigs : permissionsConfig);

      const info: UserInfo = {
        id: parsedData.data.usuario.id,
        nome: parsedData.data.usuario.nome,
        isAdmin: parsedData.data.isAdmin,
        permissions: userPermissions,
        permissionsConfig
      };
      setUserInfo(info);

      if (permissionsConfig.length > 0) {
        // ✅ NOVO: Obter JWT token para determinar perm_id inicial
        const jwtToken = TokenService.getAccessToken();
        const initialPermId = determineInitialPermId(userPermissions || [], permissionsConfig, jwtToken || undefined);

        if (initialPermId !== null) {
          const config = permissionsConfig.find((c: any) => c.perm_id === initialPermId);
          if (config) {
            // mapPermissionIdToRole retorna null quando perm_id é desconhecido
            // (já emite notify de erro). Nesse caso, não aplicamos role inválido.
            const mappedRole = mapPermissionIdToRole(initialPermId);
            if (mappedRole !== null) {
              setBaseRole(mappedRole);
              setRole(mappedRole);
              setPermissionConfig(config);
              setActivePermId(initialPermId);

              setPermissions({
                role: mappedRole,
                features: {
                  canValidate: config.features?.['general.can_validate'] ?? false,
                  canSave: config.features?.['general.can_save'] ?? false,
                  canExport: config.features?.['general.can_export'] ?? false,
                  canExportLayoutPng: config.features?.['general.can_export_layout_png'] ?? false,
                  canViewReports: config.features?.['general.can_view_reports'] ?? false,
                  canAccessTools: config.features?.['general.can_access_tools'] ?? false,
                  canAccessCalendar: config.features?.['general.can_access_calendar'] ?? false,
                  showSimplifiedInterface: config.features?.['general.show_simplified_interface'] ?? true,
                }
              });

              // Features reais carregadas — sair do loading
              const hasFeaturesLoaded = Object.keys(config.features || {}).length > 0;
              if (hasFeaturesLoaded) {
                setPermissionsLoading(false);
              }
            }
          }
        }
      }
    } catch { } finally {
      // Sempre saímos do loading após tentar hidratar, mesmo que role/config sejam null.
      // role/permissions continuam null nesses casos (notify de erro já foi emitido),
      // mas a UI sai do estado "carregando".
      setPermissionsLoading(false);
    }
  }, [determineInitialPermId]);

  // Inicializa permissões do TokenService.
  // Se readInitialAuthFromStorage() já populou o estado (usuário logado), só carrega o manifest.
  // Caso contrário (usuário não logado), valida e limpa tokens inválidos.
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    // Carrega o catálogo de features do banco de forma assíncrona (não bloqueia o login)
    loadFeaturesManifest();

    // Se o estado já foi populado pela leitura síncrona, não precisa re-ler
    if (initState !== null) return;

    try {
      const parsedData = TokenService.getUserData();
      if (parsedData) {
        // Verifica se os dados são válidos e recentes
        if (parsedData.data?.usuario?.nome && parsedData.data?.usuario?.id) {
          // ok
        } else {
          sendToTerminal('❌ [PERMISSION] Dados inválidos no TokenService');
          TokenService.clearTokens();
          return;
        }
        if (parsedData.data) {
          const userPermissions = parsedData.data.permissions;
          const permissionsConfig = parsedData.data.permissionsConfig || [];

          // Armazenar apenas configs de perm_ids conhecidos pelo frontend
          const knownConfigs = permissionsConfig.filter((c: PermissionConfig) => isValidPermissionId(c.perm_id));
          setAvailableConfigs(knownConfigs.length > 0 ? knownConfigs : permissionsConfig);

          const userInfoData = {
            id: parsedData.data.usuario.id,
            nome: parsedData.data.usuario.nome,
            login: parsedData.data.usuario.login,
            isAdmin: parsedData.data.isAdmin,
            permissions: userPermissions,
            permissionsConfig: permissionsConfig
          };

          setUserInfo(userInfoData);

          if (permissionsConfig.length > 0) {
            const jwtToken = TokenService.getAccessToken();
            const initialPermId = determineInitialPermId(userPermissions || [], permissionsConfig, jwtToken || undefined);

            if (initialPermId !== null) {
              const config = permissionsConfig.find((c: any) => c.perm_id === initialPermId);
              if (config) {
                // mapPermissionIdToRole retorna null se perm_id desconhecido
                // (já emite notify). Não aplicamos role inválido.
                const mappedRole = mapPermissionIdToRole(initialPermId);
                if (mappedRole !== null) {
                  setBaseRole(mappedRole);
                  setRole(mappedRole);
                  setPermissionConfig(config);
                  setActivePermId(initialPermId);

                  setPermissions({
                    role: mappedRole,
                    features: {
                      canValidate: config.features?.['general.can_validate'] ?? false,
                      canSave: config.features?.['general.can_save'] ?? false,
                      canExport: config.features?.['general.can_export'] ?? false,
                      canExportLayoutPng: config.features?.['general.can_export_layout_png'] ?? false,
                      canViewReports: config.features?.['general.can_view_reports'] ?? false,
                      canAccessTools: config.features?.['general.can_access_tools'] ?? false,
                      canAccessCalendar: config.features?.['general.can_access_calendar'] ?? false,
                      showSimplifiedInterface: config.features?.['general.show_simplified_interface'] ?? true,
                    }
                  });
                }
              } else {
                sendToTerminal('⚠️ [PERMISSION] Config não encontrado para perm_id', {
                  permId: initialPermId,
                  availableConfigs: permissionsConfig.map((c: any) => c.perm_id)
                });
              }
            }
            // Silencioso quando initialPermId é null ou não há configs
          }
        }
      } else {
        // silencioso
        // Não redirecionar aqui; App controla a navegação para login
      }
    } catch (error) {
      sendToTerminal('❌ [PERMISSION] Erro ao inicializar', {
        error: error instanceof Error ? error.message : 'Erro desconhecido'
      });
    } finally {
      // Sempre encerrar loading após a tentativa de inicialização — mesmo que
      // role/permissions tenham permanecido null (sem token, perm_id inválido, etc.).
      // O loading deve refletir "tentei hidratar", não "consegui resolver role".
      setPermissionsLoading(false);
    }
  }, [determineInitialPermId]);

  // Re-hidrata permissões sempre que userData do AuthContext mudar.
  // Fluxo: login/refresh/logout → AuthContext atualiza userData →
  // este effect re-deriva role/permissions a partir do TokenService
  // (que lê do mirror, mantido em sincronia com o AuthContext).
  //
  // Substituiu o antigo listener de `auth:updated` — agora reagimos
  // diretamente ao estado do AuthContext em vez de evento global.
  // `authUserData` na dep array garante reatividade.
  useEffect(() => {
    // Pular o primeiro render quando initState já populou tudo —
    // applyAuthFromStorage tem custo (logs, parses) e seria redundante.
    if (!initializedRef.current && initState !== null) return;
    applyAuthFromStorage();
  }, [authUserData, applyAuthFromStorage, initState]);

  useEffect(() => {
    // Listen for WebView2 messages
    const handleMessage = (event: MessageEvent) => {
      try {
        // 🛡️ SEGURANÇA: Validar origem da mensagem
        if (!isAllowedOrigin(event.origin)) {
          sendToTerminal('🚨 [PERMISSION] Mensagem de origem não autorizada bloqueada', {
            origin: event.origin,
            timestamp: Date.now()
          });
          return;
        }

        // Check if event.data is already an object
        if (typeof event.data === 'object' && event.data !== null) {
          const message = event.data;

          // 🛡️ VALIDAÇÃO DE SEGURANÇA: Validar mensagem antes de processar
          const validation = validateWebViewMessage(
            message,
            userInfo?.permissions?.[0]?.perm_id,
            userInfo?.isAdmin
          );

          if (!validation.isValid) {
            sendToTerminal('🚨 [PERMISSION] Mensagem WebView rejeitada', {
              reason: validation.reason,
              action: message.action
            });
            return; // ⚠️ BLOQUEAR mensagem inválida
          }

          if (message.action === 'carregarInterface' && validation.sanitizedData?.perm_id) {
            const permId = validation.sanitizedData.perm_id; // Usar dados validados
            const mappedRole = mapPermissionIdToRole(permId);

            setRole(mappedRole);

            // Se a mensagem incluir configurações de permissão validadas, atualizamos
            if (validation.sanitizedData.permissionsConfig) {
              const config = validation.sanitizedData.permissionsConfig.find(
                (c: any) => c.perm_id === permId
              );
              if (config) {
                setPermissionConfig(config);
              }
            }
          }
          return;
        }

        // If it's a string, try to parse it as JSON and validate the same way
        if (typeof event.data === 'string') {
          try {
            const message = JSON.parse(event.data);

            // Aplicar a mesma validação para mensagens JSON
            const validation = validateWebViewMessage(
              message,
              userInfo?.permissions?.[0]?.perm_id,
              userInfo?.isAdmin
            );

            if (!validation.isValid) {
              sendToTerminal('🚨 [PERMISSION] Mensagem JSON rejeitada', {
                reason: validation.reason
              });
              return;
            }

            if (message.action === 'carregarInterface' && validation.sanitizedData?.perm_id) {
              const permId = validation.sanitizedData.perm_id;
              const mappedRole = mapPermissionIdToRole(permId);
              setRole(mappedRole);

              if (validation.sanitizedData.permissionsConfig) {
                const config = validation.sanitizedData.permissionsConfig.find(
                  (c: any) => c.perm_id === permId
                );
                if (config) {
                  setPermissionConfig(config);
                }
              }
            }
          } catch (parseError) {
            sendToTerminal('❌ [PERMISSION] Erro ao fazer parse de mensagem JSON', {
              error: parseError instanceof Error ? parseError.message : 'Erro desconhecido'
            });
          }
          return;
        }

        // Silencioso
      } catch (error) {
        sendToTerminal('❌ [PERMISSION] Erro ao processar mensagem', {
          error: error instanceof Error ? error.message : 'Erro desconhecido'
        });
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Memorizações para evitar re-renders desnecessários
  const isAdminUser = useMemo(() => !!userInfo?.isAdmin, [userInfo?.isAdmin]);

  // Refs para valores lidos no effect mas que NÃO devem re-disparar ele
  const simulationLoadingRef = useRef(simulationLoading);
  simulationLoadingRef.current = simulationLoading;
  const userInfoRef = useRef(userInfo);
  userInfoRef.current = userInfo;
  // Ref para bail-out: evitar setState se valor não mudou
  const currentRoleRef = useRef(role);
  const currentPermissionsRef = useRef(permissions);

  // Helper: só chama setPermissions se algo realmente mudou
  const updatePermissionsIfChanged = useCallback((newPerms: Permission) => {
    const cur = currentPermissionsRef.current;
    if (
      cur &&
      cur.role === newPerms.role &&
      cur.features.canValidate === newPerms.features.canValidate &&
      cur.features.canSave === newPerms.features.canSave &&
      cur.features.canExport === newPerms.features.canExport &&
      cur.features.canExportLayoutPng === newPerms.features.canExportLayoutPng &&
      cur.features.canViewReports === newPerms.features.canViewReports &&
      cur.features.canAccessTools === newPerms.features.canAccessTools &&
      cur.features.canAccessCalendar === newPerms.features.canAccessCalendar &&
      cur.features.showSimplifiedInterface === newPerms.features.showSimplifiedInterface
    ) {
      return; // Nada mudou — evitar re-render
    }
    setPermissions(newPerms);
    currentPermissionsRef.current = newPerms;
  }, []);

  // Efeito principal de sincronização de permissões
  // Deps reduzidas: somente valores que DEVEM re-disparar o cálculo
  useEffect(() => {
    // effectiveRole pode ser null enquanto baseRole ainda não foi resolvido
    const effectiveRole: UserRole | null = (isAdminUser && impersonatedRole) ? impersonatedRole : baseRole;

    // Role calculation logic simplified

    // Log removido - muito verboso quando role é recalculado frequentemente
    // Mudanças de role já são logadas em changeActiveRole() quando o usuário muda manualmente

    // Bail-out: se role não mudou, evitar re-render nos 42 consumidores
    if (effectiveRole !== currentRoleRef.current) {
      setRole(effectiveRole);
      currentRoleRef.current = effectiveRole;
    }

    if (isAdminUser && impersonatedRole) {
      sendToTerminal('🎭 [SIMULATION] Simulação de fluxo ativa', {
        impersonatedRole,
        effectiveRole,
        usingRealConfig: !!simulatedConfig,
        loading: simulationLoadingRef.current
      });

      // ✅ CORREÇÃO: Usar permissões REAIS da API quando disponíveis
      if (simulatedConfig) {
        updatePermissionsIfChanged({
          role: effectiveRole,
          features: {
            canValidate: simulatedConfig.features?.['general.can_validate'] ?? false,
            canSave: simulatedConfig.features?.['general.can_save'] ?? false,
            canExport: simulatedConfig.features?.['general.can_export'] ?? false,
            canExportLayoutPng: simulatedConfig.features?.['general.can_export_layout_png'] ?? false,
            canViewReports: simulatedConfig.features?.['general.can_view_reports'] ?? false,
            canAccessTools: simulatedConfig.features?.['general.can_access_tools'] ?? false,
            canAccessCalendar: simulatedConfig.features?.['general.can_access_calendar'] ?? false,
            showSimplifiedInterface: simulatedConfig.features?.['general.show_simplified_interface'] ?? true,
          }
        });
        return;
      }

      // ✅ CORREÇÃO: Bloquear todas as permissões se API não retornar config (fail-safe)
      // Não usar fallback hardcoded - sistema deve depender 100% do backend
      sendToTerminal('⚠️ [PERMISSION] Simulação sem config da API - bloqueando permissões', {
        timestamp: Date.now(),
        source: 'FRONTEND_PERMISSION_CONTEXT',
        action: 'SIMULATION_NO_CONFIG',
        data: {
          impersonatedRole,
          effectiveRole,
          hasSimulatedConfig: !!simulatedConfig
        },
        warning: 'Simulação de role sem configuração da API. Todas as permissões foram bloqueadas por segurança.',
      });

      // Bloquear todas as permissões quando não há config da API
      updatePermissionsIfChanged({
        role: effectiveRole,
        features: {
          canValidate: false,
          canSave: false,
          canExport: false,
          canExportLayoutPng: false,
          canViewReports: false,
          canAccessTools: false,
          canAccessCalendar: false,
          showSimplifiedInterface: true
        }
      });
      return;
    }

    // Caso normal: usar permissionConfig da API para o papel base
    // Se permissionConfig existe, significa que foi inicializado
    if (permissionConfig && permissionConfig.perm_id) {
      const syncedPermissions = {
        role: effectiveRole,
        features: {
          canValidate: permissionConfig.features?.['general.can_validate'] ?? false,
          canSave: permissionConfig.features?.['general.can_save'] ?? false,
          canExport: permissionConfig.features?.['general.can_export'] ?? false,
          canExportLayoutPng: permissionConfig.features?.['general.can_export_layout_png'] ?? false,
          canViewReports: permissionConfig.features?.['general.can_view_reports'] ?? false,
          canAccessTools: permissionConfig.features?.['general.can_access_tools'] ?? false,
          canAccessCalendar: permissionConfig.features?.['general.can_access_calendar'] ?? false,
          showSimplifiedInterface: permissionConfig.features?.['general.show_simplified_interface'] ?? true,
        }
      };

      updatePermissionsIfChanged(syncedPermissions);
      noConfigLoggedRef.current = false; // Reset flag quando config é válido
    } else {
      // Sem permissionConfig - bloquear todas as permissões (fail-safe)
      const isInitializationPhase = !initializedRef.current;

      // Só loga erro crítico após inicialização e apenas uma vez
      if (!isInitializationPhase && !noConfigLoggedRef.current) {
        sendToTerminal('🚨 [PERMISSION] API não retornou config - bloqueando permissões', {
          effectiveRole,
          hasUserInfo: !!userInfoRef.current
        });
        noConfigLoggedRef.current = true;
      }

      // Bloquear todas as permissões (fail-safe)
      updatePermissionsIfChanged({
        role: effectiveRole,
        features: {
          canValidate: false,
          canSave: false,
          canExport: false,
          canExportLayoutPng: false,
          canViewReports: false,
          canAccessTools: false,
          canAccessCalendar: false,
          showSimplifiedInterface: true
        }
      });
    }
    // Deps reduzidas: simulationLoading, userInfo, availableConfigs movidos para useRef
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseRole, impersonatedRole, permissionConfig, isAdminUser, simulatedConfig, updatePermissionsIfChanged]);

  // Computar se está simulando
  const isSimulating = useMemo(() => !!(isAdminUser && impersonatedRole), [isAdminUser, impersonatedRole]);

  // Expor função de diagnóstico global (apenas em desenvolvimento)
  useEffect(() => {
    if (import.meta.env.DEV && typeof window !== 'undefined') {
      (window as any).diagnosePermissions = () => {
        try {
          const { getValidationCounters } = require('../lib/permissions/unifiedPermissionService');

          const diagnostic = {
            timestamp: new Date().toISOString(),
            permissionConfig: permissionConfig ? {
              perm_id: permissionConfig.perm_id,
              role_name: permissionConfig.role_name,
              keyFlags: {
                can_save: permissionConfig.features?.['general.can_save'],
                can_export: permissionConfig.features?.['general.can_export'],
                can_access_tools: permissionConfig.features?.['general.can_access_tools'],
                can_view_reports: permissionConfig.features?.['general.can_view_reports'],
                tool_oneregua: permissionConfig.features?.['tool.oneregua']
              }
            } : null,
            permissions: permissions ? {
              role: permissions.role,
              features: permissions.features
            } : null,
            userInfo: userInfo ? {
              id: userInfo.id,
              nome: userInfo.nome,
              isAdmin: userInfo.isAdmin
            } : null,
            role,
            baseRole,
            impersonatedRole,
            availableConfigsCount: availableConfigs.length,
            validationCounters: getValidationCounters(),
            synchronization: permissionConfig && permissions ? {
              canSave: {
                config: permissionConfig.features?.['general.can_save'],
                features: permissions.features.canSave,
                synced: permissionConfig.features?.['general.can_save'] === permissions.features.canSave
              },
              canExport: {
                config: permissionConfig.features?.['general.can_export'],
                features: permissions.features.canExport,
                synced: permissionConfig.features?.['general.can_export'] === permissions.features.canExport
              },
              canAccessTools: {
                config: permissionConfig.features?.['general.can_access_tools'],
                features: permissions.features.canAccessTools,
                synced: permissionConfig.features?.['general.can_access_tools'] === permissions.features.canAccessTools
              }
            } : null
          };

          console.log('📊 [DIAGNÓSTICO PERMISSÕES]');
          console.log('='.repeat(60));
          console.table(diagnostic.permissionConfig?.keyFlags || {});
          console.log('Sincronização:', diagnostic.synchronization);
          console.log('Contadores de Validação:', diagnostic.validationCounters);
          console.log('='.repeat(60));

          return diagnostic;
        } catch (error) {
          console.error('Erro ao obter diagnóstico:', error);
          return { error: error instanceof Error ? error.message : 'Erro desconhecido' };
        }
      };
    }
  }, [permissionConfig, permissions, userInfo, role, baseRole, impersonatedRole, availableConfigs.length]);

  // Listener WebSocket: backend emite 'permissions:refresh' após criar/revogar um override.
  // Chama /auth/me para obter permissões frescas e atualiza o estado sem exigir re-login.
  useEffect(() => {
    if (!userInfo?.id) return;
    if (!isWebSocketEnabled) return;

    const socket = socketService.getSocket({ source: 'permission-refresh', reason: 'permissions:refresh listener' });
    if (!socket) return;

    const handlePermissionsRefresh = async () => {
      try {
        const token = TokenService.getAccessToken();
        if (!token) return;
        const meResponse = await api('/auth/me', { method: 'GET' });
        const validation = validateMeResponse(meResponse, token);
        if (validation.success) {
          TokenService.setUserData(validation.normalized);
          window.dispatchEvent(new CustomEvent('auth:updated', { detail: { userData: validation.normalized } }));
          sendToTerminal('✅ [PERMISSION] permissions:refresh — permissões atualizadas em tempo real', {
            userId: userInfo.id,
            permissionsConfigCount: validation.normalized.data.permissionsConfig?.length ?? 0,
          });
        }
      } catch {
        // Falha silenciosa — próximo bootstrap já buscará /auth/me
      }
    };

    socket.on('permissions:refresh', handlePermissionsRefresh);
    return () => {
      socket.off('permissions:refresh', handlePermissionsRefresh);
    };
  }, [userInfo?.id, isWebSocketEnabled]);

  // Provider value memoizado — consumidores só re-renderizam quando um valor real muda
  const providerValue = useMemo(() => ({
    role,
    baseRole,
    impersonatedRole,
    setImpersonatedRole,
    permissions,
    permissionConfig,
    userInfo,
    setRole,
    isSimulating,
    simulationLoading,
    availableRoles,
    availableConfigs,
    canSwitchRole,
    setActiveRole,
    permissionsLoading
  }), [role, baseRole, impersonatedRole, setImpersonatedRole, permissions, permissionConfig,
    userInfo, setRole, isSimulating, simulationLoading, availableRoles, availableConfigs,
    canSwitchRole, setActiveRole, permissionsLoading]);

  return (
    <PermissionContext.Provider value={providerValue}>
      {children}
    </PermissionContext.Provider>
  );
}

/**
 * Hook para acessar permissões do usuário atual.
 * Deve ser usado dentro de PermissionProvider.
 */
export function usePermissions() {
  const context = useContext(PermissionContext);
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
}